import React from 'react';
import { Modal, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShieldHalved,
  faTriangleExclamation,
  faCheckCircle,
  faExclamationCircle,
  faTimes
} from '@fortawesome/free-solid-svg-icons';
import styles from './SystemBannerAndModal.module.css';
import { getColors } from '../utils/systemUtils';

const SystemModal = ({ show, onHide, data, type }) => {
  if (!data || !type) return null;

  const getIcon = () => {
    switch (type) {
      case 'security':
        return faShieldHalved;
      case 'outage':
      case 'alarm-alert-outage':
        return faTriangleExclamation;
      case 'resolved-outage':
        return faCheckCircle;
      case 'critical-recall':
      case 'non-critical-recall':
        return faExclamationCircle;
      default:
        return faTriangleExclamation;
    }
  };

  const getTitle = () => {
    if (data.title) return data.title;

    switch (type) {
      case 'security':
        return 'Security Alert - Unusual Activity';
      case 'outage':
        return 'System Downtime - Core Services';
      case 'resolved-outage':
        return 'System Downtime - RESOLVED';
      case 'alarm-alert-outage':
        return 'Alarm & Alert Service Outage';
      case 'critical-recall':
        return 'Device Recall - Patient Safety Alert';
      case 'non-critical-recall':
        return 'Device Update - Recommended';
      default:
        return 'System Notification';
    }
  };

  const getIncidentId = () => {
    if (data.incidentId) return `Incident ID: ${data.incidentId}`;
    if (data.recallId) return `Recall ID: ${data.recallId}`;
    if (type === 'security' && data.incidentId) return `Security ID: ${data.incidentId}`;
    return null;
  };

  const getFooterButtons = () => {
    switch (type) {
      case 'security':
        return (
          <>
            <Button className={styles.secondaryButton} onClick={onHide}>
              Contact Support
            </Button>
            <Button className={styles.primaryButton} onClick={onHide}>
              Acknowledge Incident
            </Button>
          </>
        );
      case 'critical-recall':
        return (
          <Button onClick={onHide} className={styles.primaryButton}>
            I Understand - Take Action
          </Button>
        );
      case 'resolved-outage':
        return (
          <Button onClick={onHide} className={styles.primaryButton}>
            Acknowledge Resolution
          </Button>
        );
      default:
        return (
          <button className={styles.primaryButton} onClick={onHide}>
            I Understand
          </button>
        );
    }
  };

  const getStatusBadgeClass = (status) => {
    if (!status) return '';
    const statusLower = status.toLowerCase();
    if (statusLower.includes('resolved')) {
      return styles.statusResolved;
    }
    if (statusLower.includes('investigating')) {
      return styles.statusInvestigating;
    }
    return '';
  };


  const { background, color, border } = getColors(type);

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
    >
      <Modal.Header className={styles.systemHeader} style={{ backgroundColor: background }}>
        <div className={styles.systemHeaderContent}>
          <div className={styles.systemHeaderTop}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div className={styles.systemIcon}>
                <FontAwesomeIcon icon={getIcon()} style={{ color: color }}/>
              </div>
              <div className={styles.systemTitle}>{getTitle()}</div>
            </div>
            {getIncidentId() && (
              <div className={styles.incidentId}>{getIncidentId()}</div>
            )}
          </div>
          <Button
            variant="link"
            onClick={onHide}
            className={styles.closeButton}
          >
            <FontAwesomeIcon icon={faTimes} size="lg"/>
          </Button>

        </div>

      </Modal.Header>

      <Modal.Body className={styles.systemBody}>
        <div className={styles.systemSections}>
          {data.description && (
            <div className={styles.systemSection}>
              <h6 className={styles.sectionTitle}>Description</h6>
              <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="1" viewBox="0 0 1011 1" fill="none">
  <path d="M0 0.25H1011" stroke="#B9B9B9" strokeWidth="0.5"/>
</svg>
              <p className={styles.sectionContent}>
                {data.description}
              </p>
            </div>
          )}

          {type === 'critical-recall' && data.affectedPatients && (
            <div className={styles.systemSection}>
              <h6 className={styles.sectionTitle}>Affected Patients</h6>
              <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="1" viewBox="0 0 1011 1" fill="none">
  <path d="M0 0.25H1011" stroke="#B9B9B9" strokeWidth="0.5"/>
</svg>
              <div className={styles.affectedPatientsInfo}>
                <p className={styles.sectionContent}>
                  <strong>{data.affectedPatients.count || 0} patients</strong> in your care are currently using affected devices that require immediate replacement.
                </p>
                {data.affectedPatients.count && (
                  <div className={styles.patientCountBadge}>
                    <span className={styles.count}>{data.affectedPatients.count}</span>
                    <span className={styles.label}>Patients Affected</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {data.affectedVersion && (
            <div className={styles.systemSection}>
              <h6 className={styles.sectionTitle}>Affected Version</h6>
              <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="1" viewBox="0 0 1011 1" fill="none">
  <path d="M0 0.25H1011" stroke="#B9B9B9" strokeWidth="0.5"/>
</svg>
              <div className={styles.versionBadge}>
                {data.affectedVersion}
              </div>
            </div>
          )}

          {data.impact && (
            <div className={styles.systemSection}>
              <h6 className={styles.sectionTitle}>Impact</h6>
              <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="1" viewBox="0 0 1011 1" fill="none">
  <path d="M0 0.25H1011" stroke="#B9B9B9" strokeWidth="0.5"/>
</svg>
              <p className={styles.sectionContent}>
                {data.impact}
              </p>
            </div>
          )}

          {data.impactedServices && (
            <div className={styles.systemSection}>
              <h6 className={styles.sectionTitle}>Impacted Services</h6>
              <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="1" viewBox="0 0 1011 1" fill="none">
  <path d="M0 0.25H1011" stroke="#B9B9B9" strokeWidth="0.5"/>
</svg>
              <div className={styles.servicesList}>
                {data.impactedServices.map((service, index) => (
                  <div key={index} className={styles.serviceItem}>
                    <FontAwesomeIcon icon={faExclamationCircle} className={styles.serviceIcon} />
                    <span className={styles.serviceName}>{service}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {data.status && (
            <div className={styles.systemSection}>
              <h6 className={styles.sectionTitle}>Status</h6>
              <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="1" viewBox="0 0 1011 1" fill="none">
  <path d="M0 0.25H1011" stroke="#B9B9B9" strokeWidth="0.5"/>
</svg>
              <div className={`${styles.statusBadge} ${getStatusBadgeClass(data.status)}`}>
                {data.status}
              </div>
            </div>
          )}

          {data.estimatedResolution && (
            <div className={styles.systemSection}>
              <h6 className={styles.sectionTitle}>Estimated Time to Resolution</h6>
              <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="1" viewBox="0 0 1011 1" fill="none">
  <path d="M0 0.25H1011" stroke="#B9B9B9" strokeWidth="0.5"/>
</svg>
              <div className={styles.resolutionBadge}>
                {data.estimatedResolution}
              </div>
            </div>
          )}

          {data.resolvedOn && (
            <div className={styles.systemSection}>
              <h6 className={styles.sectionTitle}>Resolved On</h6>
              <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="1" viewBox="0 0 1011 1" fill="none">
  <path d="M0 0.25H1011" stroke="#B9B9B9" strokeWidth="0.5"/>
</svg>
              <div className={styles.resolutionTimeBadge}>
                {data.resolvedOn}
              </div>
            </div>
          )}

          {data.remediationSteps && (
            <div className={styles.systemSection}>
              <h6 className={styles.sectionTitle}>Remediation Steps</h6>
              <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="1" viewBox="0 0 1011 1" fill="none">
  <path d="M0 0.25H1011" stroke="#B9B9B9" strokeWidth="0.5"/>
</svg>
              <ol className={styles.remediationList}>
                {data.remediationSteps.map((step, index) => (
                  <li key={index} className={styles.remediationItem}>
                    <span className={styles.remediationNumber}>{index + 1}.</span>
                    <span className={styles.remediationText}>{step}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {data.securityDetails && (
            <div className={styles.systemSection}>
              <h6 className={styles.sectionTitle}>Security Details</h6>
              <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="1" viewBox="0 0 1011 1" fill="none">
  <path d="M0 0.25H1011" stroke="#B9B9B9" strokeWidth="0.5"/>
</svg>
              <div className={styles.securityDetails}>
                {data.securityDetails.map((detail, index) => (
                  <div key={index} className={styles.securityDetailItem}>
                    <FontAwesomeIcon icon={faExclamationCircle} className={styles.securityDetailIcon} />
                    <span className={styles.securityDetailText}>{detail}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {data.requiredActions && (
            <div className={styles.systemSection}>
              <h6 className={styles.sectionTitle}>Required Actions</h6>
              <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="1" viewBox="0 0 1011 1" fill="none">
  <path d="M0 0.25H1011" stroke="#B9B9B9" strokeWidth="0.5"/>
</svg>
              <ol className={styles.actionsList}>
                {data.requiredActions.map((action, index) => (
                  <li key={index} className={styles.actionItem}>
                    <span className={styles.actionNumber}>{index + 1}.</span>
                    <span className={styles.actionText}>{action}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {data.supportLinks && (
            <div className={styles.systemSection}>
              <h6 className={styles.sectionTitle}>Support</h6>
              <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="1" viewBox="0 0 1011 1" fill="none">
  <path d="M0 0.25H1011" stroke="#B9B9B9" strokeWidth="0.5"/>
</svg>
              <div className={styles.supportLinks}>
                {data.supportLinks.map((link, index) => (
                  <a
                    key={index}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.supportLink}
                  >
                    {link.text}
                  </a>
                ))}
              </div>
            </div>
          )}

          {data.supportContact && (
            <div className={styles.systemSection}>
              <h6 className={styles.sectionTitle}>Support Contact</h6>
              <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="1" viewBox="0 0 1011 1" fill="none">
  <path d="M0 0.25H1011" stroke="#B9B9B9" strokeWidth="0.5"/>
</svg>
              <div className={styles.supportInfo}>
                <div className={styles.contactItem}>
                  <strong>Phone:</strong> {data.supportContact.phone}
                </div>
                <div className={styles.contactItem}>
                  <strong>Email:</strong> {data.supportContact.email}
                </div>
                <div className={styles.contactItem}>
                  <strong>Available:</strong> {data.supportContact.availability}
                </div>
              </div>
            </div>
          )}

          {data.dateIssued && (
            <div className={styles.systemSection}>
              <h6 className={styles.sectionTitle}>Date Issued</h6>
              <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="1" viewBox="0 0 1011 1" fill="none">
  <path d="M0 0.25H1011" stroke="#B9B9B9" strokeWidth="0.5"/>
</svg>
              <div className={styles.dateBadge}>
                {data.dateIssued}
              </div>
            </div>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer className={styles.modalFooter}>
        {getFooterButtons()}
      </Modal.Footer>
    </Modal>
  );
};

export default SystemModal;

